import React from 'react';
import PropTypes from 'prop-types';
import path from 'path';
import { promises as fs } from 'fs';
import i18n from 'i18next';
import { getLocaleOnSSR } from '../../utils/helpers/getLocaleOnSSR';
import { PashaLanding } from '../../components/Views/PashaLanding';
import { getSeoData } from '../../utils/helpers';
import Layout from '../../components/Layout/Layout';
import { cleanHtmlExtension } from '../../utils/helpers/middleware';
import initApollo from '../../utils/init-Apollo';
import {
  getPashaDataQuery,
  getPashaFilterDataQuery,
  getPashaItems
} from '../../api/queries/pasha';
import { ErrorPage } from '../../components/ErrorPage';
import { ThemeType } from '../../styled/theme/constants';
import { ItemConnectionSort, ItemSort } from '../../graphql/graphql';
import {
  filterFromPashaUrlToObj,
  getInitialSortOption
} from '../../utils/helpers/search';
import { removeUndefinedFields } from '../../utils/helpers/common';

export const PASHA_LANDING_SLUG = 'pasha_real_estate';

const PashaLandingPage = ({ statusCode, error, ...rest }) => {
  if (error) {
    return <ErrorPage statusCode={statusCode || 404} />;
  }

  return <PashaLanding {...rest} />;
};

PashaLandingPage.propTypes = {
  landingBrand: PropTypes.object,
  sortOption: PropTypes.object,
  pashaItems: PropTypes.array,
  totalCount: PropTypes.number,
  seoData: PropTypes.object,
  error: PropTypes.bool,
  statusCode: PropTypes.any,
  themeType: PropTypes.number,
  filtersParams: PropTypes.object,
  currentFilterValues: PropTypes.object
};

export const getServerSideProps = async context => {
  try {
    const { req, resolvedUrl, query, res } = context;

    if (query.mock === 'true' && process.env.NODE_ENV !== 'production') {
      const filePath = path.join(
        process.cwd(),
        '__mocks__',
        'pashaLandingMockData.json'
      );
      const file = await fs.readFile(filePath, 'utf-8');
      const mock = JSON.parse(file);

      return {
        props: {
          landingBrand: mock,
          themeType: ThemeType.dark,
          pashaItems: [],
          totalCount: 0,
          filtersParams: {
            categories: [],
            rooms: [],
            sort: [],
            type: []
          },
          currentFilterValues: {
            categoryId: '1',
            landingBrandSlug: 'pasha_real_estate',
            leased: false
          },
          sortOption: {
            id: '1',
            name: 'BUMPED_AT_DESC',
            params: 'bumped_at+desc'
          }
        }
      };
    }

    const locale = getLocaleOnSSR(req);

    await i18n.changeLanguage(locale);
    res?.setHeader?.('Content-Language', locale);
    const apolloClient = initApollo({}, { lang: locale });

    const currentAsPath = cleanHtmlExtension(resolvedUrl);

    const dataForTransform = await apolloClient.query({
      query: getPashaFilterDataQuery
    });

    const filter = filterFromPashaUrlToObj(dataForTransform, query);
    const sortOption = getInitialSortOption(dataForTransform.data, query);

    const variables = {
      limit: 10,
      sort: sortOption.name || ItemSort.BumpedAtDesc,
      connectionSort: sortOption.name || ItemConnectionSort.BumpedAtDesc,
      filter,
      offset: 0
    };

    const [{ data }, pashaItemsRes, seoData] = await Promise.all([
      apolloClient.query({
        query: getPashaDataQuery,
        variables: { slug: PASHA_LANDING_SLUG }
      }),
      apolloClient.query({ query: getPashaItems, variables }),
      getSeoData(apolloClient, currentAsPath, req)
    ]);

    if (!data?.landingBrand) {
      return { props: { error: true, statusCode: 404 } };
    }

    return {
      props: {
        seoData,
        landingBrand: data.landingBrand,
        pashaItems: pashaItemsRes?.data?.items || [],
        totalCount: pashaItemsRes?.data?.itemsConnection?.totalCount || 0,
        themeType: ThemeType.dark,
        filtersParams: dataForTransform.data,
        currentFilterValues: removeUndefinedFields(filter),
        sortOption
      }
    };
  } catch (error) {
    console.error('PashaLandingPage error:', error);
    return { props: { error: true, statusCode: 500 } };
  }
};

PashaLandingPage.getLayout = page => (
  <Layout isShowLocations={false}>{page}</Layout>
);

export default PashaLandingPage;
